import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../../lib/auth/hooks'
import { useI18n } from '../../lib/i18n'
import { useWebSocket } from '../../hooks/useWebSocket'
import {
  fetchFleetAlerts,
  acknowledgeAlert,
  type FleetAlert,
  type AlertSeverity,
  type AlertType,
  type AlertStatus,
} from '../../lib/api/alerts'
import { Text } from '../../../ui/components/Text'
import * as s from './FleetAlertsPage.css'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (diff < 1)   return 'Just now'
  if (diff < 60)  return `${diff}m ago`
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`
  return `${Math.floor(diff / 1440)}d ago`
}

function formatDuration(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (diff < 1)   return '<1m'
  if (diff < 60)  return `${diff}m`
  if (diff < 1440) return `${Math.floor(diff / 60)}h ${diff % 60}m`
  return `${Math.floor(diff / 1440)}d`
}

const SEVERITY_ORDER: AlertSeverity[] = ['critical', 'warning', 'info']

const SEVERITY_STYLE: Record<AlertSeverity, { dotCls: string; chipCls: string }> = {
  critical: { dotCls: s.summaryDotCritical, chipCls: s.summaryChipCritical },
  warning:  { dotCls: s.summaryDotWarning,  chipCls: s.summaryChipWarning  },
  info:     { dotCls: s.summaryDotInfo,     chipCls: s.summaryChipInfo     },
}

const SEVERITY_LABEL_KEY: Record<AlertSeverity, string> = {
  critical: 'pro.fleet.alerts.severityCritical',
  warning:  'pro.fleet.alerts.severityWarning',
  info:     'pro.fleet.alerts.severityInfo',
}

const TYPE_OPTIONS: { value: AlertType | 'all'; labelKey: string }[] = [
  { value: 'all',      labelKey: 'pro.fleet.alerts.typeAll' },
  { value: 'fault',    labelKey: 'pro.fleet.alerts.typeFaults' },
  { value: 'incident', labelKey: 'pro.fleet.alerts.typeIncidents' },
  { value: 'alert',    labelKey: 'pro.fleet.alerts.typeAlerts' },
]

const STATUS_OPTIONS: { value: AlertStatus | 'all'; labelKey: string }[] = [
  { value: 'active',       labelKey: 'pro.fleet.alerts.statusActive' },
  { value: 'acknowledged', labelKey: 'pro.fleet.alerts.statusAcknowledged' },
  { value: 'all',          labelKey: 'pro.fleet.alerts.statusAll' },
]

const TYPE_BADGE_KEY: Record<AlertType, string> = {
  fault: 'pro.fleet.alerts.badgeFault',
  incident: 'pro.fleet.alerts.badgeIncident',
  alert: 'pro.fleet.alerts.badgeAlert',
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypeBadge({ type }: { type: AlertType }) {
  const { tx } = useI18n()
  const cls =
    type === 'fault'    ? s.typeBadgeFault :
    type === 'incident' ? s.typeBadgeIncident :
                          s.typeBadgeAlert

  const icon =
    type === 'fault' ? (
      <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 4 2 21h20L12 4z" /><path d="M12 10v5M12 18h.01" />
      </svg>
    ) : type === 'incident' ? (
      <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
      </svg>
    ) : (
      <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" />
      </svg>
    )

  return (
    <span className={`${s.typeBadge} ${cls}`}>
      {icon}
      {tx(TYPE_BADGE_KEY[type])}
    </span>
  )
}

function AckButton({
  alert,
  onAcknowledge,
  pending,
}: {
  alert: FleetAlert
  onAcknowledge: (id: string) => void
  pending: boolean
}) {
  const { t, tx } = useI18n()
  if (alert.status === 'acknowledged') {
    return (
      <span className={s.ackMeta}>
        {alert.acknowledgedBy ?? t('pro.fleet.alerts.acknowledged')}
      </span>
    )
  }
  return (
    <button
      type="button"
      className={s.ackBtn}
      disabled={pending}
      onClick={() => onAcknowledge(alert.id)}
      title={t('pro.fleet.alerts.acknowledgeTitle')}
    >
      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="m5 13 4 4L19 7" />
      </svg>
      {tx('pro.fleet.alerts.acknowledge')}
    </button>
  )
}

// ─── Alert group (collapsible) ────────────────────────────────────────────────

export function AlertGroup({
  severity,
  alerts,
  pendingIds,
  onAcknowledge,
  defaultOpen,
}: {
  severity: AlertSeverity
  alerts: FleetAlert[]
  pendingIds: Set<string>
  onAcknowledge: (id: string) => void
  defaultOpen: boolean
}) {
  const { tx } = useI18n()
  const [open, setOpen] = useState(defaultOpen)
  const style = SEVERITY_STYLE[severity]

  const rowCls = (alert: FleetAlert) => {
    if (alert.status === 'acknowledged') return s.trAcknowledged
    if (severity === 'critical')         return s.trCritical
    if (severity === 'warning')          return s.trWarning
    return s.trInfo
  }

  return (
    <div className={s.group}>
      <button
        type="button"
        className={s.groupHeader}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={`${s.groupCaret} ${open ? '' : s.groupCaretCollapsed}`}>
          <svg width={12} height={12} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 16 5 9h14l-7 7z" />
          </svg>
        </span>
        <span className={`${s.summaryDot} ${style.dotCls}`} />
        <span style={{ fontWeight: 600, fontSize: 13 }}>{tx(SEVERITY_LABEL_KEY[severity])}</span>
        <span className={s.groupCount}>{alerts.length}</span>
      </button>

      {open && (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead className={s.thead}>
              <tr>
                <th className={s.th}>{tx('pro.fleet.alerts.colType')}</th>
                <th className={s.th}>{tx('pro.fleet.alerts.colMessage')}</th>
                <th className={s.th}>{tx('pro.fleet.alerts.colSite')}</th>
                <th className={s.th}>{tx('pro.fleet.alerts.colDevice')}</th>
                <th className={s.th}>{tx('pro.fleet.alerts.colFirstSeen')}</th>
                <th className={s.th}>{tx('pro.fleet.alerts.colDuration')}</th>
                <th className={`${s.th} ${s.tdActions}`} />
              </tr>
            </thead>
            <tbody>
              {alerts.map(alert => (
                <tr key={alert.id} className={rowCls(alert)}>
                  <td className={s.td}>
                    <TypeBadge type={alert.type} />
                  </td>
                  <td className={s.td}>
                    <span className={s.cellPrimary}>{alert.message}</span>
                    {alert.description && (
                      <span className={s.cellMuted}> — {alert.description}</span>
                    )}
                  </td>
                  <td className={s.td}>
                    {/* TODO: swap for <Link to="/fleet/sites/$siteId"> once site detail route exists */}
                    <a href={`/fleet/sites/${alert.siteId}`} className={s.siteLink}>
                      {alert.siteName}
                    </a>
                  </td>
                  <td className={s.td}>
                    {alert.deviceName
                      ? <span className={s.cellPrimary}>{alert.deviceName}</span>
                      : <span className={s.nullDash}>—</span>
                    }
                  </td>
                  <td className={`${s.td} ${s.cellMuted}`}>
                    {formatRelative(alert.firstSeen)}
                  </td>
                  <td className={`${s.td} ${s.cellMuted}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDuration(alert.firstSeen)}
                  </td>
                  <td className={`${s.td} ${s.tdActions}`}>
                    <AckButton
                      alert={alert}
                      onAcknowledge={onAcknowledge}
                      pending={pendingIds.has(alert.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FleetAlertsPage() {
  const { accessToken } = useAuth()
  const { t, tx } = useI18n()
  const { subscribe, unsubscribe } = useWebSocket()

  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | 'all'>('all')
  const [typeFilter,     setTypeFilter]     = useState<AlertType | 'all'>('all')
  const [statusFilter,   setStatusFilter]   = useState<AlertStatus | 'all'>('active')
  const [search,         setSearch]         = useState('')
  const [alerts,         setAlerts]         = useState<FleetAlert[]>([])
  const [pendingIds,     setPendingIds]      = useState<Set<string>>(new Set())
  const [newCount,       setNewCount]        = useState(0)
  const [refreshKey,     setRefreshKey]      = useState(0)

  // Load alerts
  useEffect(() => {
    if (!accessToken) return
    void fetchFleetAlerts(
      { status: statusFilter, type: typeFilter, search },
      accessToken,
    ).then(res => setAlerts(res.data))
  }, [accessToken, statusFilter, typeFilter, search, refreshKey])

  // WebSocket — new alerts push in without auto-refreshing the list (show banner instead)
  const handleWsMessage = useCallback(() => {
    setNewCount(n => n + 1)
  }, [])

  useEffect(() => {
    subscribe(['entity:alerts:*'], handleWsMessage)
    return () => unsubscribe(['entity:alerts:*'], handleWsMessage)
  }, [subscribe, unsubscribe, handleWsMessage])

  const handleRefreshBanner = useCallback(() => {
    setNewCount(0)
    setRefreshKey(k => k + 1)
  }, [])

  // Acknowledge
  const handleAcknowledge = useCallback(async (alertId: string) => {
    if (!accessToken) return
    setPendingIds(s => new Set(s).add(alertId))
    try {
      const { alert: updated } = await acknowledgeAlert(alertId, accessToken)
      setAlerts(prev => prev.map(a => a.id === alertId ? updated : a))
    } finally {
      setPendingIds(s => { const n = new Set(s); n.delete(alertId); return n })
    }
  }, [accessToken])

  // Derive counts for the summary strip (always from active alerts regardless of filters)
  const activeCounts = useRef<Record<AlertSeverity, number>>({ critical: 0, warning: 0, info: 0 })
  const allActive = alerts.filter(a => a.status === 'active')
  activeCounts.current = {
    critical: allActive.filter(a => a.severity === 'critical').length,
    warning:  allActive.filter(a => a.severity === 'warning').length,
    info:     allActive.filter(a => a.severity === 'info').length,
  }

  // Apply severity filter on top of already-fetched results
  const visible = severityFilter === 'all'
    ? alerts
    : alerts.filter(a => a.severity === severityFilter)

  // Group by severity
  const groups = SEVERITY_ORDER.map(sev => ({
    severity: sev,
    alerts: visible.filter(a => a.severity === sev),
  })).filter(g => g.alerts.length > 0)

  const totalActive = allActive.length

  return (
    <div className={s.page}>
      {/* Header */}
      <div className={s.pageHeader}>
        <Text level="h1" color="primary" weight="bold">{tx('pro.fleet.alerts.title')}</Text>
        <div className={s.pageHeaderActions}>
          {/* Manage Subscriptions — placeholder for notification subscription flow */}
          <button
            type="button"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
              border: '1px solid var(--color-border, #e5e7eb)',
              background: 'transparent', cursor: 'pointer', color: 'inherit',
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {tx('pro.fleet.alerts.manageSubscriptions')}
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className={s.summaryStrip}>
        <button
          type="button"
          className={`${s.summaryChip} ${s.summaryChipAll}`}
          data-active={severityFilter === 'all'}
          onClick={() => setSeverityFilter('all')}
        >
          <span className={`${s.summaryDot} ${s.summaryDotAll}`} />
          {tx('pro.fleet.alerts.severityAll')}
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{totalActive}</span>
        </button>
        {SEVERITY_ORDER.map(sev => {
          const style = SEVERITY_STYLE[sev]
          const count = activeCounts.current[sev]
          return (
            <button
              key={sev}
              type="button"
              className={`${s.summaryChip} ${style.chipCls}`}
              data-active={severityFilter === sev}
              onClick={() => setSeverityFilter(sev)}
            >
              <span className={`${s.summaryDot} ${style.dotCls}`} />
              {tx(SEVERITY_LABEL_KEY[sev])}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>
            </button>
          )
        })}
      </div>

      {/* Toolbar */}
      <div className={s.toolbar}>
        <div className={s.scopeControl}>
          {TYPE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`${s.scopeBtn}${typeFilter === opt.value ? ' is-active' : ''}`}
              onClick={() => setTypeFilter(opt.value)}
            >
              {tx(opt.labelKey)}
            </button>
          ))}
        </div>
        <div className={s.statusControl}>
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`${s.scopeBtn}${statusFilter === opt.value ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(opt.value)}
            >
              {tx(opt.labelKey)}
            </button>
          ))}
        </div>
        <div className={s.toolbarSpacer} />
        <div className={s.searchWrap}>
          <svg className={s.searchIcon} width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input
            className={s.searchInput}
            placeholder={t('pro.fleet.alerts.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* New alerts banner */}
      {newCount > 0 && (
        <div className={s.newAlertsBanner}>
          <span>
            <strong>{t('pro.fleet.alerts.newAlertsCount', { count: newCount })}</strong>
            {' '}
            {tx('pro.fleet.alerts.newAlertsSuffix')}
          </span>
          <button type="button" className={s.newAlertsBannerBtn} onClick={handleRefreshBanner}>
            {tx('pro.fleet.alerts.refresh')}
          </button>
        </div>
      )}

      {/* Content */}
      <div className={s.scrollArea}>
        {groups.length === 0 ? (
          <div className={s.allClear}>
            <div className={s.allClearIcon}>
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="m5 13 4 4L19 7" />
              </svg>
            </div>
            <div className={s.allClearTitle}>
              {search || typeFilter !== 'all' || severityFilter !== 'all'
                ? tx('pro.fleet.alerts.emptyFiltered')
                : statusFilter === 'active'
                  ? tx('pro.fleet.alerts.emptyActive')
                  : tx('pro.fleet.alerts.emptyNone')}
            </div>
            <div className={s.allClearSub}>
              {!search && typeFilter === 'all' && severityFilter === 'all' && statusFilter === 'active'
                ? tx('pro.fleet.alerts.emptyActiveSub')
                : tx('pro.fleet.alerts.emptyFilteredSub')}
            </div>
          </div>
        ) : (
          groups.map(g => (
            <AlertGroup
              key={g.severity}
              severity={g.severity}
              alerts={g.alerts}
              pendingIds={pendingIds}
              onAcknowledge={id => { void handleAcknowledge(id) }}
              defaultOpen={g.severity === 'critical' || g.severity === 'warning'}
            />
          ))
        )}
      </div>
    </div>
  )
}
